import { createContext, useContext, ReactNode, useState } from 'react';
import { useLocalStorage } from '@shared/hooks/useLocalStorage';
import { trackEvent } from '@core/events';
import { MULTI_SHOP_ENABLED } from '@core/config/shops';
import './WishlistToast.css';

export interface WishlistItem {
  productId: string;
  shopId?: string;
  name: string;
  price: number;
  imageUrl?: string;
  inStock?: boolean;
}

function matchesWishlistItem(a: WishlistItem, productId: string, shopId?: string): boolean {
  if (a.productId !== productId) return false;
  if (!MULTI_SHOP_ENABLED) return true;
  const sa = a.shopId ?? '';
  const sb = shopId ?? '';
  return sa === sb;
}

interface WishlistContextType {
  wishlistItems: WishlistItem[];
  addToWishlist: (item: WishlistItem) => void;
  removeFromWishlist: (productId: string, shopId?: string) => void;
  isInWishlist: (productId: string, shopId?: string) => boolean;
  clearWishlist: () => void;
  getWishlistItemCount: () => number;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

interface WishlistProviderProps {
  children: ReactNode;
}

export const WishlistProvider = ({ children }: WishlistProviderProps) => {
  const [wishlistItems, setWishlistItems] = useLocalStorage<WishlistItem[]>('wishlist', []);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage(null);
    }, 3000);
  };

  const addToWishlist = (item: WishlistItem) => {
    setWishlistItems((currentItems) => {
      if (currentItems.some((i) => matchesWishlistItem(i, item.productId, item.shopId))) {
        return currentItems;
      }
      return [...currentItems, item];
    });
    if (item.shopId) {
      trackEvent('wishlist_add', {
        shop_id: item.shopId,
        product_id: item.productId,
        surface: 'wishlist',
        price: item.price,
        currency: 'INR',
      });
    }
    showToast('Product added to wishlist');
  };

  const removeFromWishlist = (productId: string, shopId?: string) => {
    const existing = wishlistItems.find((item) =>
      matchesWishlistItem(item, productId, shopId)
    );
    setWishlistItems((currentItems) =>
      currentItems.filter((item) => !matchesWishlistItem(item, productId, shopId))
    );
    const resolvedShopId = existing?.shopId ?? shopId;
    if (resolvedShopId) {
      trackEvent('wishlist_remove', {
        shop_id: resolvedShopId,
        product_id: productId,
        surface: 'wishlist',
        price: existing?.price,
        currency: 'INR',
      });
    }
    showToast('Product removed from wishlist');
  };

  const isInWishlist = (productId: string, shopId?: string) => {
    return wishlistItems.some((item) => matchesWishlistItem(item, productId, shopId));
  };

  const clearWishlist = () => {
    setWishlistItems([]);
  };

  const getWishlistItemCount = () => {
    return wishlistItems.length;
  };

  return (
    <WishlistContext.Provider
      value={{
        wishlistItems,
        addToWishlist,
        removeFromWishlist,
        isInWishlist,
        clearWishlist,
        getWishlistItemCount,
      }}
    >
      {children}
      {toastMessage && (
        <div className="wishlist-toast">
          {toastMessage}
        </div>
      )}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};
